import { gql, useLazyQuery, useMutation } from "@apollo/client";
import type {
  ICustomerAccessTokenCreateMutation,
  ICustomerAddressCreateMutation,
  ICustomerAddressDeleteMutation,
  ICustomerAddressUpdateMutation,
  ICustomerCreateMutation,
  ICustomerDefaultAddressUpdateMutation,
  ICustomerQuery,
  ICustomerRecoverMutation,
  ICustomerUpdateMutation,
} from "@repo/shopify/queries/customer-gql";
import {
  customerAccessTokenCreateMutation,
  customerAddressCreateMutation,
  customerAddressDeleteMutation,
  customerAddressUpdateMutation,
  customerCreateMutation,
  customerDefaultAddressUpdateMutation,
  customerQuery,
  customerRecoverMutation,
  customerUpdateMutation,
} from "@repo/shopify/queries/customer-gql";
import { shopifyErrorHandler } from "@repo/shopify/utils/errorHandler";
import { t } from "i18next";
import { useNavigate } from "react-router-dom";

import { NOTIFICATIONS } from "@const/notifications";
import { URLS } from "@const/urls";

import useLanguage from "@hooks/useLanguage";

import { useCustomerStore } from "@store/customer";
import { useLayoutStore } from "@store/layout";

export default function useCustomerQuery() {
  const navigate = useNavigate();

  const { update, token, logout } = useCustomerStore();
  const { openToast, closeAll, openAccount } = useLayoutStore();

  const { lang } = useLanguage();

  // 로그인 stroefrontAPI 호출
  const [createToken, { loading: createTokenLoading }] = useMutation<ICustomerAccessTokenCreateMutation>(
    gql(customerAccessTokenCreateMutation.query),
    {
      onCompleted: (data) => {
        if (data.customerAccessTokenCreate.customerUserErrors.length) {
          data.customerAccessTokenCreate.customerUserErrors.forEach((error) => {
            const toastMessage = shopifyErrorHandler(error.code, lang);
            openToast({ message: toastMessage });
          });
        }
      },
      onError: (error) => {
        openToast(NOTIFICATIONS.ERROR_SERVER);
        throw new Error(error.message);
      },
    },
  );

  // 회원가입 storefrontAPI 호출
  const [createCustomer, { loading: createCustomerLoading }] = useMutation<ICustomerCreateMutation>(
    gql(customerCreateMutation.query),
    {
      onCompleted: async ({ customerCreate }) => {
        if (customerCreate.customerUserErrors.length) {
          customerCreate.customerUserErrors.forEach((error) => {
            const toastMessage = shopifyErrorHandler(error.code, lang);
            openToast({ message: toastMessage });
          });
          return;
        }

        openToast({ message: t("success.create_account") });
      },
      onError: (error) => {
        openToast(NOTIFICATIONS.ERROR_SERVER);
        throw new Error(error.message);
      },
    },
  );

  // 비밀번호 찾기 storefrontAPI 호출
  const [resetPassword, { loading: resetPasswordLoading }] = useMutation<ICustomerRecoverMutation>(
    gql(customerRecoverMutation.query),
    {
      onCompleted: ({ customerRecover }) => {
        // 가입한 계정이 아닌 경우
        if (customerRecover.customerUserErrors.length) {
          customerRecover.customerUserErrors.forEach((error) => {
            const toastMessage = shopifyErrorHandler(error.code, lang);
            openToast({ message: toastMessage });
          });

          return;
        }

        closeAll();
        openToast({ message: t("success.password_reset_email") });
      },
      onError: (error) => {
        openToast(NOTIFICATIONS.ERROR_SERVER);
        throw new Error(error.message);
      },
    },
  );

  // 회원 정보 조회 storefrontAPI 호출
  const [fetchCustomer] = useLazyQuery<ICustomerQuery>(gql(customerQuery.query), {
    fetchPolicy: "no-cache",
    onCompleted: ({ customer }) => {
      if (!customer) {
        openToast(NOTIFICATIONS.WARNING_END_SESSION);
        logout();
        navigate(URLS.HOME);
        setTimeout(() => {
          openAccount(true);
        }, 300);
        return;
      }
      update(customer);
    },
    onError: (error) => {
      openToast(NOTIFICATIONS.ERROR_SERVER);
      throw new Error(error.message);
    },
  });

  // 회원 정보 업데이트 storefrontAPI 호출
  const [updateCustomer, { loading: updateCustomerLoading }] = useMutation<ICustomerUpdateMutation>(
    gql(customerUpdateMutation.query),
    {
      onCompleted: ({ customerUpdate }) => {
        if (customerUpdate.customerUserErrors.length) {
          customerUpdate.customerUserErrors.forEach((error) => {
            const toastMessage = shopifyErrorHandler(error.code, lang);
            openToast({ message: toastMessage });
          });

          return;
        }

        update(customerUpdate.customer);
      },
      onError: (error) => {
        openToast(NOTIFICATIONS.ERROR_SERVER);
        throw new Error(error.message);
      },
    },
  );

  const [changePassword] = useMutation<ICustomerUpdateMutation>(gql(customerUpdateMutation.query), {
    fetchPolicy: "no-cache",
    onCompleted: (data) => {
      if (data?.customerUpdate.customerUserErrors.length) {
        data.customerUpdate.customerUserErrors.forEach((error) => {
          const toastMessage = shopifyErrorHandler(error.code, lang);
          openToast({ message: toastMessage });
        });
        return;
      }

      if (data?.customerUpdate.customerAccessToken?.accessToken) {
        openToast({ message: t("success.reset_password") });
      }
    },
    onError: (error) => {
      openToast(NOTIFICATIONS.ERROR_SERVER);
      throw new Error(error.message);
    },
  });

  const [setDefaultAddress, { loading: setDefaultAddressLoading }] = useMutation<ICustomerDefaultAddressUpdateMutation>(
    gql(customerDefaultAddressUpdateMutation.query),
    {
      variables: { customerAccessToken: token.accessToken },
      onCompleted: (data) => {
        if (data.customerDefaultAddressUpdate.customerUserErrors.length) {
          data.customerDefaultAddressUpdate.customerUserErrors.forEach((error) => {
            const toastMessage = shopifyErrorHandler(error.code, lang);
            openToast({ message: toastMessage });
          });
        }
      },
      onError: (error) => {
        openToast(NOTIFICATIONS.ERROR_SERVER);
        throw new Error(error.message);
      },
    },
  );

  const [createAddress, { loading: createAddressLoading }] = useMutation<ICustomerAddressCreateMutation>(
    gql(customerAddressCreateMutation.query),
    {
      variables: { customerAccessToken: token.accessToken },
      onCompleted: (data) => {
        if (data.customerAddressCreate.customerUserErrors.length) {
          data.customerAddressCreate.customerUserErrors.forEach((error) => {
            const toastMessage = shopifyErrorHandler(error.code, lang);
            openToast({ message: toastMessage });
          });
        }
      },
      onError: (error) => {
        openToast(NOTIFICATIONS.ERROR_SERVER);
        throw new Error(error.message);
      },
    },
  );

  const [editAddress, { loading: editAddressLoading }] = useMutation<ICustomerAddressUpdateMutation>(
    gql(customerAddressUpdateMutation.query),
    {
      variables: { customerAccessToken: token.accessToken },
      onCompleted: (data) => {
        if (data.customerAddressUpdate.customerUserErrors.length) {
          data.customerAddressUpdate.customerUserErrors.forEach((error) => {
            const toastMessage = shopifyErrorHandler(error.code, lang);
            openToast({ message: toastMessage });
          });
        }
      },
      onError: (error) => {
        openToast(NOTIFICATIONS.ERROR_SERVER);
        throw new Error(error.message);
      },
    },
  );

  const [deleteAddress, { loading: deleteAddressLoading }] = useMutation<ICustomerAddressDeleteMutation>(
    gql(customerAddressDeleteMutation.query),
    {
      variables: { customerAccessToken: token.accessToken },
      onCompleted: (data) => {
        if (data.customerAddressDelete.customerUserErrors.length) {
          data.customerAddressDelete.customerUserErrors.forEach((error) => {
            const toastMessage = shopifyErrorHandler(error.code, lang);
            openToast({ message: toastMessage });
          });

          return;
        }

        openToast(NOTIFICATIONS.SUCCESS_ADDRESS_REMOVE);
      },
      onError: (error) => {
        openToast(NOTIFICATIONS.ERROR_ADDRESS_REMOVE);
        throw new Error(error.message);
      },
    },
  );

  return {
    createToken,
    createCustomer,
    resetPassword,
    fetchCustomer,
    updateCustomer,
    changePassword,
    setDefaultAddress,
    createAddress,
    editAddress,
    deleteAddress,
    updateCustomerLoading,
    createTokenLoading,
    createCustomerLoading,
    resetPasswordLoading,
    setDefaultAddressLoading,
    createAddressLoading,
    editAddressLoading,
    deleteAddressLoading,
  };
}
